
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { AIProviderSelector } from "./AIProviderSelector";
import { makeAIApiCall } from "../services/aiService";
import { cleanKPIResponse } from "../utils/kpiUtils";

type KPIInputSectionProps = {
  companyTickerSymbol: string;
  setCompanyTickerSymbol: (symbol: string) => void;
  isLoadingKPIs: boolean;
  setIsLoadingKPIs: (loading: boolean) => void;
  hasKPIContent: boolean;
  setHasKPIContent: (hasContent: boolean) => void;
  aiProvider: string;
  setAiProvider: (provider: string) => void;
  setRawKPIResponse: (response: string) => void;
  setKpiData: (data: any) => void;
  selectedAccount: any;
  canEdit: boolean;
};

export const KPIInputSection = ({
  companyTickerSymbol,
  setCompanyTickerSymbol,
  isLoadingKPIs,
  setIsLoadingKPIs,
  hasKPIContent,
  setHasKPIContent,
  aiProvider,
  setAiProvider,
  setRawKPIResponse,
  setKpiData,
  selectedAccount,
  canEdit
}: KPIInputSectionProps) => {
  const [showNetworkAlert, setShowNetworkAlert] = useState(false);

  const retrieveKPIs = async () => {
    if (!companyTickerSymbol) {
      toast.error('Please enter a company ticker symbol');
      return;
    }
    
    if (!selectedAccount?.company_id) {
      toast.error('Please select an account first');
      return;
    }

    if (!canEdit) {
      toast.error('Only account owners can retrieve new KPI data');
      return;
    }
    
    setIsLoadingKPIs(true);
    setHasKPIContent(false);
    
    try {
      const content = await makeAIApiCall(companyTickerSymbol, aiProvider);
      setRawKPIResponse(content);
      
      const processedData = cleanKPIResponse(content);
      
      // Ensure exactly 5 items per category
      const ensureExactly5Items = (items: string[]) => {
        if (items.length === 0) {
          return ['No data available', 'No data available', 'No data available', 'No data available', 'No data available'];
        }
        if (items.length < 5) {
          const padding = Array(5 - items.length).fill('Additional data not available');
          return [...items, ...padding];
        }
        return items.slice(0, 5); // Take only first 5 if more than 5
      };
      
      const finalData = {
        executiveKPIs: ensureExactly5Items(processedData.executiveKPIs || []),
        coreFinancialKPIs: ensureExactly5Items(processedData.coreFinancialKPIs || []),
        businessInitiatives: ensureExactly5Items(processedData.businessInitiatives || []),
        operatingKPIs: ensureExactly5Items(processedData.operatingKPIs || [])
      };
      
      setKpiData(finalData);
      setHasKPIContent(true);
      
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      setShowNetworkAlert(true);
      setHasKPIContent(false);
    } finally {
      setIsLoadingKPIs(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <Input 
          type="text" 
          placeholder="Enter company ticker symbol" 
          value={companyTickerSymbol} 
          onChange={e => setCompanyTickerSymbol(e.target.value)} 
          className="bg-white text-black w-64"
          disabled={!canEdit}
        />
        <Button 
          onClick={retrieveKPIs} 
          disabled={isLoadingKPIs || !companyTickerSymbol || !canEdit}
        >
          {isLoadingKPIs ? 'Retrieving KPIs...' : 'Retrieve KPIs using AI'}
        </Button>
        
        <AIProviderSelector 
          aiProvider={aiProvider} 
          setAiProvider={setAiProvider} 
          disabled={!canEdit}
        />
        
        {isLoadingKPIs ? (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        ) : (
          !hasKPIContent && <span className="text-[12px] text-primary-light">(may require several tries)</span>
        )}
      </div>



      <AlertDialog open={showNetworkAlert} onOpenChange={setShowNetworkAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Network Issue</AlertDialogTitle>
            <AlertDialogDescription>
              Network conditions are affecting the query. Please try again, or try using a different AI engine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowNetworkAlert(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
